/**
 * Anonymous chatbot session: cookie with session id + local interaction count (UX).
 * Server enforces daily limits via HttpOnly `chat_session_id` and counters; this keeps UI in sync across reloads.
 */

const DEFAULT_PUBLIC_MAX_INTERACTIONS = 5;

function readPublicMaxInteractionsFromEnv(): number {
  const raw = import.meta.env.CHATBOT_PUBLIC_MAX_INTERACTIONS;
  if (raw === undefined || raw === "") {
    return DEFAULT_PUBLIC_MAX_INTERACTIONS;
  }
  const n = parseInt(String(raw), 10);
  return Number.isFinite(n) && n > 0 ? n : DEFAULT_PUBLIC_MAX_INTERACTIONS;
}

export const CHATBOT_PUBLIC_COOKIE_NAME = "evoly_chatbot_pub";
/** Same env var name as admin-api: `CHATBOT_PUBLIC_MAX_INTERACTIONS` (see `vite.config` envPrefix). */
export const CHATBOT_PUBLIC_MAX_INTERACTIONS = readPublicMaxInteractionsFromEnv();
const COOKIE_MAX_AGE_SEC = 2 * 24 * 60 * 60;

function utcDateKey(): string {
  return new Date().toISOString().slice(0, 10);
}

export interface PublicChatbotCookie {
  sessionId: string;
  interactionCount: number;
  /** UTC calendar day (YYYY-MM-DD); resets interactionCount when the day changes */
  dayKey: string;
}

function parseDocumentCookies(): Record<string, string> {
  if (typeof document === "undefined") {
    return {};
  }
  return document.cookie.split(";").reduce(
    (acc, part) => {
      const idx = part.indexOf("=");
      if (idx === -1) return acc;
      const key = part.slice(0, idx).trim();
      const val = part.slice(idx + 1).trim();
      if (key) {
        try {
          acc[key] = decodeURIComponent(val);
        } catch {
          acc[key] = val;
        }
      }
      return acc;
    },
    {} as Record<string, string>,
  );
}

function normalizePublicSession(
  parsed: Partial<PublicChatbotCookie> & { sessionId?: string },
): PublicChatbotCookie {
  const today = utcDateKey();
  const sid =
    typeof parsed.sessionId === "string" && parsed.sessionId.length > 0
      ? parsed.sessionId
      : crypto.randomUUID();
  const prevDay = typeof parsed.dayKey === "string" ? parsed.dayKey : "";
  const count =
    typeof parsed.interactionCount === "number" &&
    Number.isFinite(parsed.interactionCount) &&
    parsed.interactionCount >= 0 &&
    prevDay === today
      ? parsed.interactionCount
      : 0;
  return {
    sessionId: sid,
    interactionCount: Math.min(count, CHATBOT_PUBLIC_MAX_INTERACTIONS),
    dayKey: today,
  };
}

function readRawCookie(): PublicChatbotCookie | null {
  const raw = parseDocumentCookies()[CHATBOT_PUBLIC_COOKIE_NAME];
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<PublicChatbotCookie>;
    if (typeof parsed.sessionId === "string" && parsed.sessionId.length > 0) {
      return normalizePublicSession(parsed);
    }
  } catch {
    return null;
  }
  return null;
}

function writeRawCookie(data: PublicChatbotCookie): void {
  if (typeof document === "undefined") return;
  const payload = encodeURIComponent(JSON.stringify(data));
  const secure = import.meta.env.PROD ? "; Secure" : "";
  document.cookie = `${CHATBOT_PUBLIC_COOKIE_NAME}=${payload}; Path=/; Max-Age=${COOKIE_MAX_AGE_SEC}; SameSite=Lax${secure}`;
}

/** Load valid cookie or create a new session id and persist it (count starts at 0 for today). */
export function getOrCreatePublicChatbotSession(): PublicChatbotCookie {
  const existing = readRawCookie();
  if (existing) {
    return existing;
  }
  const today = utcDateKey();
  const next: PublicChatbotCookie = {
    sessionId: crypto.randomUUID(),
    interactionCount: 0,
    dayKey: today,
  };
  writeRawCookie(next);
  return next;
}

export function incrementPublicChatbotInteraction(): PublicChatbotCookie | null {
  const cur = readRawCookie();
  if (!cur) return null;
  const today = utcDateKey();
  const base =
    cur.dayKey === today
      ? cur
      : { ...cur, interactionCount: 0, dayKey: today };
  const next: PublicChatbotCookie = {
    ...base,
    interactionCount: Math.min(
      base.interactionCount + 1,
      CHATBOT_PUBLIC_MAX_INTERACTIONS,
    ),
    dayKey: today,
  };
  writeRawCookie(next);
  return next;
}

export function readPublicChatbotSession(): PublicChatbotCookie | null {
  return readRawCookie();
}
